/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { EntryPoint, EntryPointComponent, JSResourceReference } from 'react-relay';
import type { HubsDataPreloadProviderEntrypointProps } from '@jira/servicedesk__solution-composer-embeds/src/HubsDataPreloadProvider.entrypoint.types.tsx';
import { HelpHelpCenterAri } from '@atlassian/ari/help/help-center';
import type { hubHomeContentWrapperQuery } from '@atlassian/hub-home/HubHomeContentWrapperQueryParameters';
import HubHomeContentWrapperQueryParameters from '@atlassian/hub-home/HubHomeContentWrapperQueryParameters$parameters';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

type HubsDataExtraProps = {
	cloudId?: string;
	projectKey?: string;
	helpCenterId?: string;
	hubAri?: string;
	pageId?: string;
};

function getPortalPageAriId(portalId: string): string {
	return `portal_page-${portalId}`;
}

/**
 * Extracts the helpCenterId string from a raw ARI or plain ID from the URL.
 * Returns an empty string if the ARI is malformed and no plain hubId is available.
 */
function extractHelpCenterIdFromAri(hubAri: string): string {
	try {
		return HelpHelpCenterAri.parse(hubAri).helpCenterId;
	} catch {
		return '';
	}
}

function hubHomeContentQueryAris(
	activationId: string,
	hubIdFromUrl: string,
	pageId?: string,
	hubAriFromUrl?: string,
): { hubAri: string; pageAri: string } {
	const createPageAri = (resolvedActivationId: string, resolvedHubId: string): string =>
		pageId
			? `ari:cloud:help::page/${resolvedActivationId}/${getPortalPageAriId(pageId)}/${resolvedHubId}`
			: `ari:cloud:help::page/${resolvedActivationId}/${resolvedHubId}`;

	try {
		const hub = hubAriFromUrl
			? HelpHelpCenterAri.parse(hubAriFromUrl)
			: HelpHelpCenterAri.parse(hubIdFromUrl);
		return {
			hubAri: hub.toString(),
			pageAri: createPageAri(hub.activationId, hub.helpCenterId),
		};
	} catch {
		return {
			hubAri: hubAriFromUrl ?? `ari:cloud:help::help-center/${activationId}/${hubIdFromUrl}`,
			pageAri: createPageAri(activationId, hubIdFromUrl),
		};
	}
}

type HubsDataPreloadEntryPoint = EntryPointComponent<
	{ hubHomeContentQuery: hubHomeContentWrapperQuery },
	Record<string, never>,
	HubsDataPreloadProviderEntrypointProps,
	HubsDataExtraProps
>;

const hubsRoot: JSResourceReference<HubsDataPreloadEntryPoint> = JSResourceForUserVisible(
	/* webpackChunkName: "async-servicedesk-getting-started-hubs", jiraSpaEntry: "async-servicedesk-getting-started-hubs" */ '@jira/servicedesk__solution-composer-embeds/src/HubsDataPreloadProvider.tsx',
	{ entryFsSsrLiftImportToModule: true },
);

export const gettingStartedHubsEntryPoint: EntryPoint<
	HubsDataPreloadEntryPoint,
	EntryPointRouteParams
> = createEntryPoint<JSResourceReference<HubsDataPreloadEntryPoint>, EntryPointRouteParams>({
	root: hubsRoot,
	getPreloadProps: (params: EntryPointRouteParams) => {
		const {
			context: { match, query },
			tenantContext: { cloudId, activationId },
		} = params;
		const projectKey = match?.params?.projectKey;
		if (!projectKey || !cloudId) {
			return {
				extraProps: { cloudId: '', projectKey: '' },
			};
		}
		const hubIdRaw = query?.hubId;
		const hubId = typeof hubIdRaw === 'string' ? hubIdRaw : null;
		const hubAriRaw = query?.hubAri;
		const hubAri = typeof hubAriRaw === 'string' ? hubAriRaw : undefined;
		const pageIdRaw = query?.pageId;
		const pageId = typeof pageIdRaw === 'string' ? pageIdRaw : undefined;

		if (!hubId && !hubAri) {
			return {
				extraProps: { cloudId, projectKey },
			};
		}

		const resolvedHelpCenterId = hubId ?? (hubAri ? extractHelpCenterIdFromAri(hubAri) : '');

		const { hubAri: resolvedHubAri, pageAri } = hubHomeContentQueryAris(
			activationId,
			resolvedHelpCenterId,
			pageId,
			hubAri,
		);

		return {
			queries: {
				hubHomeContentQuery: {
					parameters: HubHomeContentWrapperQueryParameters,
					variables: {
						cloudId,
						hubAri: resolvedHubAri,
						hubEditMode: false,
						operationType: 'READ',
						pageAri,
					},
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
				},
			},
			extraProps: {
				cloudId,
				projectKey,
				helpCenterId: resolvedHelpCenterId,
				hubAri: resolvedHubAri,
				pageId,
			},
		};
	},
});
